// ============================================================================
// EXPORT REPORTS - AUDITOR PAGE
// ============================================================================
// Purpose: Generate and export audit reports in various formats
// Mental model: "Let me create professional reports for stakeholders."
// ============================================================================

import React, { useState, useEffect } from 'react';
import { auditApi } from '../../audit/api';
import PageInfoButton from '@/components/PageInfoButton';

const ExportReports: React.FC = () => {
  const [reports, setReports] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [assessments, setAssessments] = useState<any[]>([]);
  const [exportOptions, setExportOptions] = useState({
    format: 'pdf',
    includeExecutiveSummary: true,
    includeDetailedFindings: true,
    includeEvidence: false,
    includeRecommendations: true,
    company: '',
    framework: '',
    assessmentId: '',
    dateRange: 'last_quarter'
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Load assessments
      const assessmentsData = await auditApi.getAssessmentSummaries();
      setAssessments(assessmentsData);

      // For now, keep empty reports list since there's no API to get generated reports
      setReports([]);
    } catch (error) {
      console.error('Failed to load data:', error);
      setAssessments([]);
      setReports([]);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateReport = async () => {
    if (!exportOptions.assessmentId) {
      alert('Please select an assessment to generate a report for.');
      return;
    }

    setGenerating(true);
    try {
      const response = await auditApi.generateAssessmentReport(
        parseInt(exportOptions.assessmentId),
        exportOptions.format as 'pdf' | 'excel' | 'word'
      );

      const newReport = {
        id: Date.now(), // Use timestamp as ID since we don't have real report ID
        name: `${exportOptions.framework} Assessment Report - ${exportOptions.company}`,
        company: exportOptions.company,
        framework: exportOptions.framework,
        generated_at: new Date().toISOString(),
        format: exportOptions.format.toUpperCase(),
        size: 'Generated',
        status: 'completed',
        download_url: response.downloadUrl
      };

      setReports([newReport, ...reports]);
      setExportOptions({
        format: 'pdf',
        includeExecutiveSummary: true,
        includeDetailedFindings: true,
        includeEvidence: false,
        includeRecommendations: true,
        company: '',
        framework: '',
        assessmentId: '',
        dateRange: 'last_quarter'
      });
    } catch (error) {
      console.error('Failed to generate report:', error);
      alert('Failed to generate report. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'text-green-600 bg-green-100';
      case 'generating': return 'text-blue-600 bg-blue-100';
      case 'failed': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getFormatIcon = (format: string) => {
    switch (format.toLowerCase()) {
      case 'pdf': return 'picture_as_pdf';
      case 'excel': return 'table_chart';
      case 'word': return 'description';
      default: return 'file_present';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center gap-1"><h1 className="text-3xl font-bold text-slate-900">Export Reports</h1><PageInfoButton /></div>
        <p className="text-slate-600 mt-1">
          Generate and download comprehensive audit reports
        </p>
      </div>

      {/* Generate New Report */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">Generate New Report</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Assessment</label>
            <select
              value={exportOptions.assessmentId}
              onChange={(e) => {
                const selectedAssessment = assessments.find(a => a.id.toString() === e.target.value);
                setExportOptions({
                  ...exportOptions,
                  assessmentId: e.target.value,
                  company: selectedAssessment ? selectedAssessment.company_name : '',
                  framework: selectedAssessment ? selectedAssessment.framework_name : ''
                });
              }}
              className="w-full p-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select Assessment</option>
              {assessments.map((assessment) => (
                <option key={assessment.id} value={assessment.id}>
                  {assessment.framework_name} - {assessment.company_name} ({assessment.status})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Format</label>
            <select
              value={exportOptions.format}
              onChange={(e) => setExportOptions({ ...exportOptions, format: e.target.value })}
              className="w-full p-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="pdf">PDF Document</option>
              <option value="excel">Excel Spreadsheet</option>
              <option value="word">Word Document</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Format</label>
            <select
              value={exportOptions.format}
              onChange={(e) => setExportOptions({ ...exportOptions, format: e.target.value })}
              className="w-full p-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="pdf">PDF Document</option>
              <option value="excel">Excel Spreadsheet</option>
              <option value="word">Word Document</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Date Range</label>
            <select
              value={exportOptions.dateRange}
              onChange={(e) => setExportOptions({ ...exportOptions, dateRange: e.target.value })}
              className="w-full p-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="last_quarter">Last Quarter</option>
              <option value="last_month">Last Month</option>
              <option value="last_year">Last Year</option>
              <option value="custom">Custom Range</option>
            </select>
          </div>
        </div>

        <div className="mt-6">
          <h3 className="text-sm font-medium text-slate-700 mb-3">Report Sections</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={exportOptions.includeExecutiveSummary}
                onChange={(e) => setExportOptions({ ...exportOptions, includeExecutiveSummary: e.target.checked })}
                className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-slate-700">Executive Summary</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={exportOptions.includeDetailedFindings}
                onChange={(e) => setExportOptions({ ...exportOptions, includeDetailedFindings: e.target.checked })}
                className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-slate-700">Detailed Findings</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={exportOptions.includeEvidence}
                onChange={(e) => setExportOptions({ ...exportOptions, includeEvidence: e.target.checked })}
                className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-slate-700">Evidence Documentation</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={exportOptions.includeRecommendations}
                onChange={(e) => setExportOptions({ ...exportOptions, includeRecommendations: e.target.checked })}
                className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-slate-700">Recommendations</span>
            </label>
          </div>
        </div>

        <div className="mt-6">
          <button
            onClick={handleGenerateReport}
            disabled={generating || !exportOptions.assessmentId}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-slate-400 disabled:cursor-not-allowed transition-colors"
          >
            {generating ? (
              <div className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Generating Report...
              </div>
            ) : (
              'Generate Report'
            )}
          </button>
        </div>
      </div>

      {/* Generated Reports */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">Generated Reports</h2>

        {reports.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <span className="material-icons-outlined text-4xl mb-2">description</span>
            <p>No reports generated yet</p>
          </div>
        ) : (
          <div className="space-y-4">
            {reports.map((report) => (
              <div key={report.id} className="flex items-center justify-between p-4 border border-slate-200 rounded-lg">
                <div className="flex items-center gap-4">
                  <span className={`material-icons-outlined text-2xl ${report.format === 'PDF' ? 'text-red-500' : 'text-green-500'}`}>
                    {getFormatIcon(report.format)}
                  </span>
                  <div>
                    <h3 className="font-medium text-slate-900">{report.name}</h3>
                    <p className="text-sm text-slate-600">
                      Generated {new Date(report.generated_at).toLocaleDateString()} • {report.size}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(report.status)}`}>
                    {report.status}
                  </span>
                  <button className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors">
                    Download
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Report Templates */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">Report Templates</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 border border-slate-200 rounded-lg hover:border-blue-300 transition-colors cursor-pointer">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mb-3">
              <span className="material-icons-outlined text-blue-600">assessment</span>
            </div>
            <h3 className="font-medium text-slate-900 mb-1">Full Assessment Report</h3>
            <p className="text-sm text-slate-600">Complete audit report with all findings and evidence</p>
          </div>

          <div className="p-4 border border-slate-200 rounded-lg hover:border-blue-300 transition-colors cursor-pointer">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mb-3">
              <span className="material-icons-outlined text-green-600">summarize</span>
            </div>
            <h3 className="font-medium text-slate-900 mb-1">Executive Summary</h3>
            <p className="text-sm text-slate-600">High-level overview for management</p>
          </div>

          <div className="p-4 border border-slate-200 rounded-lg hover:border-blue-300 transition-colors cursor-pointer">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mb-3">
              <span className="material-icons-outlined text-purple-600">bug_report</span>
            </div>
            <h3 className="font-medium text-slate-900 mb-1">Findings Only</h3>
            <p className="text-sm text-slate-600">Detailed list of all findings and issues</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExportReports;